import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { Crown, Lock, ArrowRight, Maximize2, Minimize2, Clock, LayoutGrid } from "lucide-react";
import { getTerminalCandles, type Candle } from "@/lib/terminal-candles.functions";
import { useIntel } from "@/components/dashboard/terminal/DeskIntel";
import { useEconomicCalendar } from "@/hooks/useEconomicCalendar";
import { useWeekendHold } from "@/hooks/useWeekendHold";
import { GoldChart } from "@/components/dashboard/terminal/GoldChart";
import { chime, hms, DeskWire, SetupRadar } from "@/components/dashboard/terminal/RoomWidgets";
import { useAuth } from "@/lib/auth-context";
import { useDeskSound } from "@/hooks/useDeskSound";
import { useIsAdmin } from "@/hooks/useIsAdmin";
import { useGoldPrice } from "@/hooks/useGoldPrice";
import { useLiveSignals } from "@/hooks/useSignalsData";
import { useMarketStats } from "@/components/dashboard/island/hooks/useMarketStats";
import { MacroLensPulseCard } from "@/components/dashboard/MacroLensPulseCard";
import { MacroStackCard } from "@/components/dashboard/terminal/MacroStackCard";
import { SeasonalityCard } from "@/components/dashboard/SeasonalityCard";
import { MacroSnapshotCard } from "@/components/dashboard/MacroSnapshotCard";
import { MacroDock } from "@/components/dashboard/terminal/EliteLiquidDock";
import { EliteLayoutEditor } from "@/components/dashboard/terminal/EliteLayoutEditor";
import { Button } from "@/components/ui/button";
import { StructureLensUpload } from "@/components/dashboard/terminal/CommandWidgets";
import { RiskCockpit, Checklist, MacroShield } from "@/components/dashboard/terminal/RoomWidgets";
import { PriceLevels, StructureAlignment, PerformanceSpectrum } from "@/components/dashboard/terminal/MarketStation";
import { BattleMapPanel, LiquidityTracker, ProximityRadar, SessionPlaybook } from "@/components/dashboard/terminal/DeskTools";
import { VolatilityPulse } from "@/components/dashboard/terminal/VolatilityPulse";
import { PositionSizeCalculator } from "@/components/tools/PositionSizeCalculator";
import { DEFAULT_ELITE_LAYOUT, ELITE_CARDS, LAYOUT_STORAGE_KEY, getLayoutPositions, normalizeLayout, type CardSpan, type EliteCardId, type LayoutItem } from "@/lib/elite-workspace";
import { resolveClimate, climateTone } from "@/components/dashboard/command/marketClimate";

export const Route = createFileRoute("/_authenticated/dashboard/")({
  head: () => ({
    meta: [
      { title: "XAU Terminal™ | XAU Pro Academy" },
      { name: "description", content: "Unified institutional gold terminal: macro confluence, multi-timeframe structure, session liquidity and the Orion live desk on one screen." },
      { property: "og:title", content: "XAU Terminal™ | XAU Pro Academy" },
      { property: "og:description", content: "Macro, structure, liquidity and the Orion live desk on one institutional screen." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: EliteTerminal,
});

function useNow(ms = 1000) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), ms);
    return () => window.clearInterval(id);
  }, [ms]);
  return now;
}


type TF = "M5" | "M15" | "H1" | "H4" | "D1";
const TF_STEP: Record<TF, number> = { M5: 300, M15: 900, H1: 3600, H4: 14400, D1: 86400 };

function nextHighImpact(events: { title: string; date: string }[] | null | undefined, now: number) {
  const up = (events ?? [])
    .map((e) => ({ ...e, t: Date.parse(e.date) }))
    .filter((e) => Number.isFinite(e.t) && e.t > now - 30 * 60_000)
    .sort((x, y) => x.t - y.t)[0];
  if (!up) return null;
  const mins = (up.t - now) / 60_000;
  const state = mins <= 30 && mins >= -30 ? "freeze" : mins <= 120 ? "caution" : "clear";
  return { title: up.title, ms: Math.max(0, up.t - now), state } as const;
}

/** Live chart with timeframe switch, desk overlay and expand, reused from the Command Center. */
function EliteChart({ price }: { price: number | null }) {
  const fetchCandles = useServerFn(getTerminalCandles);
  const [tf, setTf] = useState<TF>("M15");
  const [overlay, setOverlay] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const { data: intel } = useIntel();
  const weekend = useWeekendHold();
  const { data } = useQuery({
    queryKey: ["terminal-candles", tf],
    queryFn: () => fetchCandles({ data: { tf } }),
    refetchInterval: 60_000,
  });
  useEffect(() => {
    if (!expanded) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setExpanded(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [expanded]);
  const candles = data?.candles ?? [];
  const chartCandles = useMemo(() => {
    if (weekend.closed || price == null || candles.length === 0) return candles;
    const step = TF_STEP[tf];
    const off = tf === "D1" ? 7200 : 0;
    const bucket = Math.floor((Date.now() / 1000 + off) / step) * step - off;
    const latest = candles.at(-1)!;
    const live: Candle = latest.t === bucket
      ? { ...latest, h: Math.max(latest.h, price), l: Math.min(latest.l, price), c: price }
      : { t: bucket, o: latest.c, h: Math.max(latest.c, price), l: Math.min(latest.c, price), c: price };
    return latest.t === bucket ? [...candles.slice(0, -1), live] : [...candles.slice(-119), live];
  }, [candles, price, tf, weekend.closed]);

  return (
    <>
      {expanded && <div aria-hidden onClick={() => setExpanded(false)} className="fixed inset-0 z-40 bg-background/85 backdrop-blur-xl" />}
      <section className={`min-w-0 rounded-lg border border-gold/30 bg-card/50 p-4 backdrop-blur-xl ${expanded ? "fixed left-1/2 top-1/2 z-50 w-[94vw] max-w-[1400px] -translate-x-1/2 -translate-y-1/2 bg-card shadow-2xl" : ""}`}>
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-gold">XAUUSD Desk Chart</div>
          <div className="flex flex-wrap gap-1">
            {(["M5", "M15", "H1", "H4", "D1"] as const).map((k) => (
              <button key={k} onClick={() => setTf(k)}
                className={`rounded border px-2 py-0.5 font-mono text-[10px] ${tf === k ? "border-gold bg-gold-muted text-gold" : "border-border/60 text-muted-foreground"}`}>{k}</button>
            ))}
            <button onClick={() => setOverlay((v) => !v)} aria-pressed={overlay}
              className={`rounded border px-2 py-0.5 font-mono text-[10px] ${overlay ? "border-gold bg-gold-muted text-gold" : "border-border/60 text-muted-foreground"}`}>Desk layer</button>
            <button onClick={() => setExpanded((v) => !v)} aria-label={expanded ? "Close expanded chart" : "Expand chart"}
              className="rounded border border-border/60 px-1.5 py-0.5 text-muted-foreground hover:border-gold/60 hover:text-gold">
              {expanded ? <Minimize2 className="h-3.5 w-3.5" /> : <Maximize2 className="h-3.5 w-3.5" />}
            </button>
          </div>
        </div>
        <GoldChart candles={chartCandles} dayOpen={data?.dayOpen ?? null} intel={overlay ? intel ?? null : null} tall={expanded} />
      </section>
    </>
  );
}

const SPAN_CLASS: Record<CardSpan, string> = {
  4: "@[720px]:col-span-4",
  6: "@[720px]:col-span-6",
  8: "@[720px]:col-span-8",
  12: "@[720px]:col-span-12",
};

function useEliteLayout(isAdmin: boolean) {
  const [layout, setLayout] = useState<LayoutItem[]>(() => DEFAULT_ELITE_LAYOUT.map((x) => ({ ...x })));
  useEffect(() => {
    if (!isAdmin) return;
    try {
      const raw = window.localStorage.getItem(LAYOUT_STORAGE_KEY);
      if (raw) setLayout(normalizeLayout(JSON.parse(raw)));
    } catch { /* keep default */ }
  }, [isAdmin]);
  const update = (l: LayoutItem[]) => {
    setLayout(l);
    try { window.localStorage.setItem(LAYOUT_STORAGE_KEY, JSON.stringify(l)); } catch { /* ignore */ }
  };
  return [isAdmin ? layout : DEFAULT_ELITE_LAYOUT, update] as const;
}

function EliteDesk({ price, pct, now, userId, isAdmin }: { price: number | null; pct: number | null; now: number; userId?: string; isAdmin: boolean }) {
  const { data: intel } = useIntel();
  const { events } = useEconomicCalendar();
  const { data: signals } = useLiveSignals(userId);
  const open = useMemo(() => (signals ?? []).filter((s) => s.tradeStatus !== "Closed"), [signals]);
  // Mobile only: lead with the radar when an open trade has levels this viewer can see.
  const radarFirstOnMobile = open.some((s) => s.entry != null && s.sl != null);
  const weekend = useWeekendHold();
  const [layout, setLayout] = useEliteLayout(isAdmin);
  const [editing, setEditing] = useState(false);
  const positions = getLayoutPositions(layout);
  const fundamentalsRef = useRef<HTMLDivElement | null>(null);
  const [dockOpacity, setDockOpacity] = useState(1);
  useEffect(() => {
    const el = fundamentalsRef.current;
    if (!el) return;
    let frame = 0;
    const update = () => {
      frame = 0;
      const rect = el.getBoundingClientRect();
      // Start at the former five-percent intersection boundary.
      const start = window.innerHeight - rect.height * 0.05;
      const distance = Math.min(180, window.innerHeight * 0.22);
      const progress = Math.max(0, Math.min(1, (start - rect.top) / distance));
      setDockOpacity(1 - progress * progress);
    };
    const schedule = () => {
      if (!frame) frame = window.requestAnimationFrame(update);
    };
    const observer = new ResizeObserver(schedule);
    observer.observe(el);
    window.addEventListener("scroll", schedule, { passive: true, capture: true });
    window.addEventListener("resize", schedule);
    update();
    return () => {
      observer.disconnect();
      window.removeEventListener("scroll", schedule, true);
      window.removeEventListener("resize", schedule);
      window.cancelAnimationFrame(frame);
    };
  }, []);

  const render = (id: EliteCardId): React.ReactNode => {
    switch (id) {
      case "chart": return <EliteChart price={price} />;
      case "macrolens": return (
        <MacroStackCard slides={[
          { id: "pulse", label: "MacroLens Pulse", node: <MacroLensPulseCard /> },
          { id: "shield", label: "Macro Shield", node: <MacroShield events={events} now={now} /> },
          { id: "volatility", label: "Volatility Pulse", node: <VolatilityPulse nowMs={now} /> },
        ]} />
      );
      case "orion": return <SetupRadar signals={open} loading={signals === undefined} price={price} weekend={weekend.closed} />;
      case "deskwire": return <DeskWire reading={intel?.reading} />;
      case "structure": return <StructureAlignment />;
      case "liquidity": return (
        <MacroStackCard label="Liquidity and options map" slides={[
          { id: "liquidity", label: "Liquidity Tracker", node: <LiquidityTracker price={price} className="pb-9" /> },
          { id: "battle-map", label: "Visual Battle Map", node: <BattleMapPanel price={price} className="pb-9" /> },
        ]} />
      );
      case "levels": return <PriceLevels price={price} />;
      case "radar": return <ProximityRadar price={price} />;
      case "performance": return <PerformanceSpectrum />;
      case "session": return <SessionPlaybook nowMs={now} />;
      case "volatility": return <VolatilityPulse nowMs={now} />;
      case "calendar": return <MacroShield events={events} now={now} />;
      case "structurelens": return <StructureLensUpload />;
      case "calculator": return <PositionSizeCalculator goldOnly />;
      case "risk": return <RiskCockpit />;
      case "checklist": return <Checklist />;
    }
  };

  return (
    <div className="elite-desk @container space-y-6">
      {isAdmin && (
        <div className="flex justify-end">
          <Button variant="outlineGold" size="sm" type="button" onClick={() => setEditing((value) => !value)} aria-expanded={editing}
            className="font-mono text-[10px] uppercase tracking-[0.2em]">
            <LayoutGrid className="h-3.5 w-3.5" strokeWidth={1.6} /> Customize workspace
          </Button>
        </div>
      )}

      <div dir="ltr" className="grid grid-cols-1 items-stretch gap-6 @[720px]:grid-cols-12">
        {layout.filter((x) => x.visible).map((x) => (
          <div key={x.id} data-workspace-card={x.id} className={`flex min-w-0 flex-col ${SPAN_CLASS[x.span]} ${x.id === "orion" && radarFirstOnMobile ? "-order-1 @[720px]:order-none" : ""}`}>
            {editing && <div className="mb-2 flex flex-wrap items-center justify-between gap-1 font-mono text-[9px] text-gold">
              <span>{ELITE_CARDS.find((card) => card.id === x.id)?.label}</span>
              <span className="hidden @[720px]:inline">{positions.get(x.id)?.label}</span>
            </div>}
            <div className="flex min-h-0 flex-1 flex-col [&>*]:flex-1">{render(x.id)}</div>
          </div>
        ))}
      </div>


      {/* Fixed fundamentals block. Always the last thing on the page on every
          screen, and never part of the editable workspace layout. The floating
          Macro Dock fades with scroll progress without unmounting its reports. */}
      <div ref={fundamentalsRef} className="space-y-6 pb-6">
        <h2 className="font-mono text-xs uppercase tracking-[0.2em] text-gold">
          Fundamentals & historical context
        </h2>
        {/* Half-width each on desktop: seasonality and macro intelligence
            side by side; full-width stacked on tablet and mobile. */}
        <div className="grid grid-cols-1 items-stretch gap-6 @[900px]:grid-cols-2">
          <SeasonalityCard />
          <MacroSnapshotCard />
        </div>
      </div>
      <div className="lg:hidden">
        <MacroDock opacity={dockOpacity} />
      </div>

      {isAdmin && <EliteLayoutEditor open={editing} onOpenChange={setEditing} layout={layout} onChange={setLayout} />}
    </div>
  );
}

const SESSIONS = [
  { name: "Asian Range", short: "Asia", startH: 22, endH: 7, note: "Range formation. Low participation." },
  { name: "London", short: "London", startH: 7, endH: 12, note: "Primary liquidity window." },
  { name: "London / NY Overlap", short: "London / NY Overlap", startH: 12, endH: 16, note: "Peak participation." },
  { name: "New York", short: "New York", startH: 16, endH: 21, note: "Continuation and closing flows." },
  { name: "Market Quiet", short: "Market Quiet", startH: 21, endH: 22, note: "Transition. No execution value." },
] as const;

function inWin(h: number, w: (typeof SESSIONS)[number]) {
  return w.startH <= w.endH ? h >= w.startH && h < w.endH : h >= w.startH || h < w.endH;
}

const TRACK = [
  { s: 0, e: 7, label: "Asia", tone: "var(--muted-foreground)", a: 18 },
  { s: 7, e: 12, label: "London", tone: "var(--gold)", a: 45 },
  { s: 12, e: 16, label: "Overlap", tone: "var(--gold)", a: 75 },
  { s: 16, e: 21, label: "New York", tone: "var(--gold)", a: 40 },
  { s: 22, e: 24, label: "", tone: "var(--muted-foreground)", a: 18 },
];

/** Elite HUD: current session, named countdown to the next one, price and 24h UTC track. */
function EliteHud({ now, price, delta, pct, utc, weekend }: { now: number; price: number | null; delta: number | null; pct: number | null; utc: string; weekend: ReturnType<typeof useWeekendHold> }) {
  const d = new Date(now);
  const h = d.getUTCHours();
  const idx = Math.max(0, SESSIONS.findIndex((w) => inWin(h, w)));
  const cur = SESSIONS[idx];
  const next = SESSIONS[(idx + 1) % SESSIONS.length];
  const target = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate(), cur.endH));
  if (target.getTime() <= now) target.setUTCDate(target.getUTCDate() + 1);
  const pos = ((h * 60 + d.getUTCMinutes()) / 1440) * 100;
  const up = (delta ?? 0) >= 0;
  if (weekend.closed) {
    return (
      <header className="rounded-lg border border-gold/30 bg-card/60 p-5 backdrop-blur-xl">
        <div className="flex items-center justify-between gap-3 font-mono text-[10px] uppercase tracking-[0.24em]">
          <span className="flex items-center gap-2 text-gold"><Crown className="h-3.5 w-3.5" strokeWidth={1.6} /> XAU Terminal™ · Standby</span>
          <span className="tabular-nums text-muted-foreground">{utc} UTC</span>
        </div>
        <h1 className="mt-3 font-display text-3xl">Weekend Hold</h1>
        <p className="mt-1 text-sm text-muted-foreground">Market closed. Desk on standby until the Sunday reopen.</p>
        <div className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div className="rounded-md border border-border/60 bg-background/40 p-4">
            <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
              <Clock className="h-3.5 w-3.5 shrink-0" /> Reopens in
            </div>
            <div className="mt-2 font-mono text-3xl tabular-nums md:text-4xl">{hms(weekend.msToOpen)}</div>
            <div className="mt-2 font-mono text-[10px] uppercase tracking-[0.16em] text-muted-foreground">Sunday 22:00 UTC · Asian open</div>
          </div>
          <div className="rounded-md border border-border/60 bg-background/40 p-4">
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Friday close · XAUUSD</div>
            <div className="mt-2 font-mono text-3xl tabular-nums md:text-4xl">{price?.toFixed(2) ?? "..."}</div>
            <div className="mt-2 font-mono text-[10px] uppercase tracking-[0.16em] tabular-nums" style={{ color: up ? "var(--bias-bull)" : "var(--bias-bear)" }}>
              {delta != null && pct != null ? `${up ? "+" : ""}${delta.toFixed(2)} (${up ? "+" : ""}${pct.toFixed(2)}%) on the day` : "..."}
            </div>
          </div>
        </div>
      </header>
    );
  }
  return (
    <header className="rounded-lg border border-gold/30 bg-card/60 p-5 backdrop-blur-xl">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.24em] text-gold">
            <Crown className="h-3.5 w-3.5" strokeWidth={1.6} /> XAU Terminal™ · {weekend.closed ? "Standby" : "Live"}
          </div>
          <h1 className="mt-2 font-display text-3xl">{weekend.closed ? "Weekend Hold" : cur.name}</h1>
          <p className="mt-1 text-sm text-muted-foreground">{weekend.closed ? "Market closed. Desk on standby until the Sunday reopen." : cur.note}</p>
          <div className="mt-2 flex flex-wrap items-center gap-2 font-mono text-xs text-muted-foreground">
            <Clock className="h-3.5 w-3.5 shrink-0" /> {weekend.closed ? "Market reopens in" : `${next.short} in`} <span className="tabular-nums text-foreground">{hms(weekend.closed ? weekend.msToOpen : target.getTime() - now)}</span>
          </div>
          {weekend.closed && <p className="mt-1 font-mono text-[10px] text-muted-foreground">Sunday 22:00 UTC</p>}
        </div>
        <div className="text-right font-mono tabular-nums">
          <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">XAUUSD · {utc} UTC</div>
          <div className="mt-1 text-4xl md:text-5xl">{price?.toFixed(2) ?? "..."}</div>
          <div className="mt-1 text-sm" style={{ color: up ? "var(--bias-bull)" : "var(--bias-bear)" }}>
            {delta != null && pct != null ? `${up ? "+" : ""}${delta.toFixed(2)} (${up ? "+" : ""}${pct.toFixed(2)}%) ${weekend.closed ? "Friday close" : "today"}` : "..."}
          </div>
        </div>
      </div>
      {!weekend.closed && <div className="mt-5">
        <div className="relative h-7">
          <div className="absolute inset-x-0 top-1/2 h-px -translate-y-1/2 bg-border/60" />
          {TRACK.map((g) => (
            <div key={g.s} className="absolute top-1/2 h-1.5 -translate-y-1/2 rounded-full"
              style={{ left: `${(g.s / 24) * 100}%`, width: `calc(${((g.e - g.s) / 24) * 100}% - 2px)`, background: `color-mix(in oklab, ${g.tone} ${g.a}%, transparent)` }} />
          ))}
          <div className="absolute top-0 h-full" style={{ left: `${pos}%` }}>
            <div className="h-full w-px -translate-x-1/2 bg-foreground/80" />
            <div className="absolute left-0 top-1/2 h-2.5 w-2.5 -translate-x-1/2 -translate-y-1/2 rounded-full bg-gold shadow-[0_0_12px_var(--gold)]" />
          </div>
        </div>
        <div className="relative mt-1 h-3 font-mono text-[9px] uppercase tracking-[0.16em] text-muted-foreground">
          {TRACK.filter((g) => g.label).map((g) => (
            <span key={g.s} className="absolute hidden -translate-x-1/2 sm:inline" style={{ left: `${((g.s + g.e) / 2 / 24) * 100}%` }}>{g.label}</span>
          ))}
          <span className="absolute left-0">00</span>
          <span className="absolute right-0">24 UTC</span>
        </div>
      </div>}
    </header>
  );
}

function EliteTerminal() {
  const { user, profile } = useAuth();
  const { isAdmin } = useIsAdmin();
  const unlocked = true; // XAU Terminal is open to every member. Locked levels are withheld server side.
  const now = useNow();
  const weekend = useWeekendHold(1000);
  const { price } = useGoldPrice(15_000);
  const stats = useMarketStats();
  const prev = stats.snapshot?.previousClose ?? null;
  const delta = price != null && prev ? price - prev : null;
  const pct = delta != null && prev ? (delta / prev) * 100 : null;
  const utc = new Date(now).toISOString().slice(11, 19);
  const { events } = useEconomicCalendar();

  // Audio alerts: preference lives in the account menu, chime when open positions change.
  const { sound } = useDeskSound();
  const { data: signals } = useLiveSignals(unlocked ? user?.id : undefined);
  const sigRef = useRef("");
  useEffect(() => {
    if (!unlocked) return;
    const sig = (signals ?? []).filter((s) => s.tradeStatus !== "Closed").map((s) => `${s.id}:${s.tradeStatus}:${s.status ?? ""}`).join("|");
    if (sigRef.current && sig !== sigRef.current && sound) chime();
    sigRef.current = sig;
  }, [signals, sound, unlocked]);

  // Live market climate, same shared resolver as Command Center and Trading Room.
  const tone = climateTone(resolveClimate(pct));

  return (
    <div className="relative min-h-screen overflow-hidden" style={{ ["--tone" as string]: tone }}>
      <div aria-hidden className="pointer-events-none fixed inset-0 z-0 transition-[background] duration-700"
        style={{ background: "radial-gradient(ellipse 70% 45% at 50% -5%, color-mix(in oklab, var(--tone) 20%, transparent), transparent 70%)" }} />
      <div aria-hidden className="orion-hero-breath pointer-events-none fixed -top-24 right-[8%] z-0 h-64 w-96 rounded-full blur-[90px]"
        style={{ background: "color-mix(in oklab, var(--tone) 22%, transparent)" }} />
      <div aria-hidden className="orion-hero-breath pointer-events-none fixed top-[30%] -left-24 z-0 h-72 w-72 rounded-full blur-[110px]"
        style={{ background: "color-mix(in oklab, var(--tone) 10%, transparent)", animationDelay: "-4s" }} />
      <div className="relative z-10 container mx-auto max-w-[1600px] space-y-4 px-4 py-6">
        <EliteHud now={now} price={price} delta={delta} pct={pct} utc={utc} weekend={weekend} />

        <div className="relative">
          {unlocked ? (
            <EliteDesk price={price} pct={pct} now={now} userId={user?.id} isAdmin={isAdmin} />
          ) : (
            // Locked preview: static placeholder shapes only. No live data is
            // fetched or rendered, so removing the blur reveals nothing.
            <div aria-hidden className="elite-desk pointer-events-none select-none grid gap-4 opacity-60 blur-[6px] xl:grid-cols-3">
              {[0, 1, 2].map((col) => (
                <div key={col} className="space-y-4">
                  {[0, 1, 2].map((i) => (
                    <section key={i} className="rounded-lg border p-4">
                      <div className="h-2 w-24 rounded bg-gold/30" />
                      <div className="mt-4 space-y-2">
                        <div className="h-3 w-full rounded bg-muted/40" />
                        <div className="h-3 w-4/5 rounded bg-muted/40" />
                        <div className="h-3 w-3/5 rounded bg-muted/40" />
                      </div>
                    </section>
                  ))}
                </div>
              ))}
            </div>
          )}


          {!unlocked && (
            <div className="absolute inset-0 flex items-start justify-center pt-24">
              <div className="max-w-md rounded-lg border border-gold/40 bg-card/90 p-6 text-center shadow-gold backdrop-blur-xl">
                <Lock className="mx-auto h-6 w-6 text-gold" strokeWidth={1.6} />
                <div className="mt-3 font-mono text-[10px] uppercase tracking-[0.24em] text-gold">Elite access only</div>
                <h2 className="mt-2 font-display text-2xl">One screen. Every layer.</h2>
                <p className="mt-2 text-sm text-muted-foreground">
                  Macro confluence, multi-timeframe structure, session liquidity and the Orion live desk, unified for the Elite plan.
                </p>
                <Link to="/pricing" className="mt-5 inline-flex items-center gap-2 rounded-md bg-gold px-4 py-2 text-sm font-medium text-primary-foreground">
                  View Elite membership <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
